import { useState, useEffect, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";
import { ThemeProvider } from "@/hooks/useTheme";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import InsightsHomePage from "@/pages/InsightsHomePage";
import ResourcesPage from "@/pages/ResourcesPage";
import InsightsPage from "@/pages/InsightsPage";
import SystemPage from "@/pages/SystemPage";
import CostsPage from "@/pages/CostsPage";
import CleanupWizardPage from "@/pages/CleanupWizardPage";
import DocsPage from "@/pages/DocsPage";
import BillingPage from "@/pages/BillingPage";
import AdminPage from "@/pages/AdminPage";
import ChangeManagementPage from "@/pages/ChangeManagementPage";
import HygienePage from "@/pages/HygienePage";
import DesignPreviewPage from "@/pages/DesignPreviewPage";
import DeploymentPage from "@/pages/DeploymentPage";
import DeploymentWizardPage from "@/pages/DeploymentWizardPage";
import EnterpriseDataPlanePage from "@/pages/EnterpriseDataPlanePage";
import Layout from "@/components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1`;

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);
export { API };

const ROLE_PERMISSIONS = {
  viewer: new Set([
    "resources.read", "costs.read", "insights.read", "feed.read",
    "settings.read", "credentials.read", "scan.status", "docs.read", "audit.self",
  ]),
  user: new Set([
    "resources.read", "costs.read", "insights.read", "feed.read",
    "settings.read", "credentials.read", "credentials.write",
    "scan.trigger", "scan.status", "insights.generate",
    "cleanup.read", "cleanup.execute", "docs.read",
    "audit.self", "billing.self",
  ]),
  operator: new Set([
    "resources.read", "costs.read", "insights.read", "feed.read",
    "settings.read", "settings.write",
    "credentials.read", "credentials.write", "credentials.delete",
    "scan.trigger", "scan.status", "insights.generate",
    "cleanup.read", "cleanup.execute", "docs.read",
    "data.clear", "audit.self", "audit.all", "billing.self",
    "email.status", "email.log",
  ]),
  admin: new Set([
    "resources.read", "costs.read", "insights.read", "feed.read",
    "settings.read", "settings.write",
    "credentials.read", "credentials.write", "credentials.delete",
    "scan.trigger", "scan.status", "insights.generate",
    "cleanup.read", "cleanup.execute", "docs.read",
    "data.clear", "audit.self", "audit.all",
    "billing.self", "billing.manage",
    "admin.users", "admin.plans", "admin.gates", "admin.sso",
    "email.status", "email.log", "email.test",
  ]),
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("niyant_token"));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem("niyant_token");
    setToken(null);
    setUser(null);
  }, []);

  const api = useCallback(() => {
    const instance = axios.create({
      baseURL: API,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    
    // Add response interceptor to handle token expiration
    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid - clear auth and redirect to login
          const errorMsg = error.response?.data?.detail || "";
          const errorCode = error.response?.data?.code || "";
          
          // Only redirect for actual authentication failures, not authorization failures
          const isAuthFailure = 
            errorMsg.includes("expired") || 
            errorMsg.includes("invalid") || 
            errorMsg.includes("Token") ||
            errorCode === "token_expired" ||
            errorCode === "invalid_token";
          
          if (isAuthFailure) {
            console.warn("Token expired, redirecting to login");
            localStorage.removeItem("niyant_token");
            setToken(null);
            setUser(null);
            // Redirect to login page only if not already there
            if (window.location.pathname !== "/login") {
              window.location.href = "/login?session=expired";
            }
          }
        }
        return Promise.reject(error);
      }
    );
    
    return instance;
  }, [token]);

  useEffect(() => {
    if (token) {
      api()
        .get("/auth/me")
        .then((res) => {
          setUser(res.data);
          setLoading(false);
        })
        .catch(() => {
          localStorage.removeItem("niyant_token");
          setToken(null);
          setUser(null);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [token, api]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const accessToken = res.data.access_token || res.data.token;
    localStorage.setItem("niyant_token", accessToken);
    setToken(accessToken);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (email, password, name) => {
    const res = await axios.post(`${API}/auth/register`, {
      email,
      password,
      name,
    });
    const accessToken = res.data.access_token || res.data.token;
    localStorage.setItem("niyant_token", accessToken);
    setToken(accessToken);
    setUser(res.data.user);
    return res.data;
  };

  // logout is already defined above as useCallback

  const hasPermission = useCallback(
    (perm) => {
      const role = user?.role || "viewer";
      const perms = ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS.viewer;
      return perms.has(perm);
    },
    [user]
  );

  return (
    <AuthContext.Provider
      value={{ user, token, loading, login, register, logout, api, hasPermission }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="flex items-center justify-center h-screen bg-[var(--n-bg)]">
        <div className="niyant-spinner" />
      </div>
    );
  if (!user) return <Navigate to="/login" />;
  return children;
};

function App() {
  return (
    <div className="App">
      <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<InsightsHomePage />} />
                      <Route path="/dashboard-legacy" element={<DashboardPage />} />
                      <Route path="/resources" element={<ResourcesPage />} />
                      <Route path="/changes" element={<ChangeManagementPage />} />
                      <Route path="/costs" element={<CostsPage />} />
                      <Route path="/hygiene" element={<HygienePage />} />
                      <Route path="/cleanup" element={<CleanupWizardPage />} />
                      <Route path="/insights" element={<InsightsPage />} />
                      <Route path="/system" element={<SystemPage />} />
                      <Route path="/settings" element={<Navigate to="/system" replace />} />
                      <Route path="/docs" element={<DocsPage />} />
                      <Route path="/billing" element={<BillingPage />} />
                      <Route path="/admin" element={<AdminPage />} />
                      <Route path="/deployments" element={<DeploymentPage />} />
                      <Route path="/deployments/new" element={<DeploymentWizardPage />} />
                      <Route path="/enterprise/data-plane" element={<EnterpriseDataPlanePage />} />
                      <Route path="/design-preview" element={<DesignPreviewPage />} />
                    </Routes>
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
      </ThemeProvider>
    </div>
  );
}

export default App;
